import { Navigate, Route, Routes } from 'react-router-dom';
import type { AdminRole } from '@insurensa/shared';
import type { ReactNode } from 'react';
import { useAuth } from './lib/auth.js';
import { Layout } from './components/Layout.js';
import { AppLoader } from './components/Loader.js';
import { LoginPage } from './pages/Login.js';
import { DashboardPage } from './pages/Dashboard.js';
import { PartnersPage } from './pages/Partners.js';
import { PartnerDetailPage } from './pages/PartnerDetail.js';
import { LeadDetailPage, LeadsPage } from './pages/Leads.js';
import { UserDetailPage, UsersPage } from './pages/Users.js';
import { ReviewsPage } from './pages/Reviews.js';
import { AppReleasesPage } from './pages/AppReleases.js';
import { TeamPage } from './pages/Team.js';
import { AuditLogPage } from './pages/AuditLog.js';

/**
 * Route guard. This is a usability measure, not the security boundary — the
 * API enforces the same rules independently, so a hand-typed URL gets a 403
 * from the server rather than data.
 */
function RequireAuth({ roles, children }: { roles?: AdminRole[]; children: ReactNode }): JSX.Element {
  const { admin, loading } = useAuth();

  if (loading) {
    // Nothing of the shell is rendered yet, so centre on the viewport.
    return <AppLoader />;
  }

  if (!admin) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(admin.role)) return <Navigate to="/" replace />;

  return <>{children}</>;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="partners/:id" element={<PartnerDetailPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="app-releases" element={<AppReleasesPage />} />
        <Route
          path="team"
          element={
            <RequireAuth roles={['SUPER_ADMIN']}>
              <TeamPage />
            </RequireAuth>
          }
        />
        <Route
          path="audit-log"
          element={
            <RequireAuth roles={['SUPER_ADMIN', 'ADMIN']}>
              <AuditLogPage />
            </RequireAuth>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
